import { useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  not_generated: { label: "Not Generated", tone: "bg-slate-100 text-slate-500", icon: "Circle" },
  queued: { label: "Queued", tone: "bg-slate-100 text-slate-600", icon: "Clock" },
  generating: { label: "Generating", tone: "bg-blue-100 text-blue-700", icon: "Loader2", spin: true },
  ready: { label: "Ready", tone: "bg-emerald-100 text-emerald-700", icon: "CheckCircle2" },
  review_available: { label: "Review Available", tone: "bg-amber-100 text-amber-700", icon: "AlertTriangle" },
  superseded: { label: "Superseded", tone: "bg-slate-100 text-slate-400", icon: "Archive" },
  failed: { label: "Failed", tone: "bg-red-100 text-red-700", icon: "XCircle" },
};
const SEV = { info: "text-slate-500", attention: "text-amber-600", warning: "text-red-600" };
const CONF = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-500" };

/** Phase 2D-i — Document Intelligence. Allison reasons from the DIP (never raw files): Summary,
 * Extracted Facts, Observations, each evidence-backed, with the AI Decision Trace. */
export default function DocumentIntelligenceCard({ docId }) {
  const [pkg, setPkg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [aiReady, setAiReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const poll = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/documents-intel/${docId}/intelligence`);
      setPkg(data.package); setAiReady(data.ai_ready); setLoaded(true);
      const st = data.package?.intelligence_status;
      if ((st === "queued" || st === "generating") && !poll.current) {
        poll.current = setInterval(load, 4000);
      } else if (st !== "queued" && st !== "generating" && poll.current) {
        clearInterval(poll.current); poll.current = null;
      }
    } catch { /* ignore */ }
  }, [docId]);

  useEffect(() => { load(); return () => { if (poll.current) clearInterval(poll.current); }; }, [load]);

  const regenerate = async () => {
    setBusy(true);
    try { await api.post(`/documents-intel/${docId}/intelligence/regenerate`); toast.success("Intelligence regeneration queued"); load(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.message || (typeof d === "string" ? d : "Not eligible yet")); }
    finally { setBusy(false); }
  };

  const st = STATUS[pkg?.intelligence_status] || (loaded ? STATUS.not_generated : { label: "Loading…", tone: "bg-slate-100 text-slate-400", icon: "Loader2", spin: true });
  const StIcon = Icons[st.icon] || Icons.Circle;
  const summary = pkg?.document_summary;
  const facts = pkg?.facts || [];
  const obs = pkg?.observations || [];
  const trace = pkg?.intelligence_decision_trace;
  const ready = pkg?.intelligence_status === "ready";

  return (
    <div className="border-b border-slate-100 pb-3" data-testid="document-intelligence-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
          <Icons.BrainCircuit className="h-3.5 w-3.5" />Document Intelligence
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${st.tone}`} data-testid="intelligence-status">
            <StIcon className={`h-3 w-3 ${st.spin ? "animate-spin" : ""}`} />{st.label}
          </span>
          {aiReady && (
            <button onClick={regenerate} disabled={busy} data-testid="intelligence-regenerate" title="Regenerate Intelligence" className="text-[10px] border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50">
              <Icons.RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />Regenerate
            </button>
          )}
        </div>
      </div>

      {pkg?.intelligence_status === "not_generated" && (
        <div className="text-xs text-slate-400" data-testid="intelligence-empty">
          Intelligence is generated automatically once the document is AI-Ready and classified. Reasoned from the Document Intelligence Package.
        </div>
      )}
      {(pkg?.intelligence_status === "queued" || pkg?.intelligence_status === "generating") && (
        <div className="text-xs text-slate-500" data-testid="intelligence-progress">Allison is reasoning over the package…</div>
      )}
      {pkg?.intelligence_status === "failed" && (
        <div className="text-xs text-red-600" data-testid="intelligence-failed">Generation failed — use Regenerate to retry.</div>
      )}

      {ready && (
        <div className="space-y-3" data-testid="intelligence-detail">
          {/* Section 1 — Summary */}
          {summary && (
            <div data-testid="intelligence-summary">
              <div className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Summary</div>
              <div className="flex flex-wrap gap-1.5 text-[11px]">
                {summary.classification_label && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">{summary.classification_label}</span>}
                {summary.tax_year && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">TY {summary.tax_year}</span>}
                {summary.jurisdiction && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{summary.jurisdiction}</span>}
                {summary.language && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{summary.language}</span>}
                {(summary.entities || []).map((e, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{e.name}{e.type ? ` · ${e.type}` : ""}</span>)}
              </div>
            </div>
          )}

          {/* Section 2 — Facts */}
          <div data-testid="intelligence-facts">
            <div className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Extracted Facts ({facts.length})</div>
            <div className="space-y-1">
              {facts.map((f) => (
                <div key={f.id} className="flex items-start gap-1.5 text-[11px]" data-testid="intelligence-fact">
                  <span className={`text-[9px] px-1 rounded mt-0.5 ${CONF[f.confidence] || "bg-slate-100"}`}>{f.confidence}</span>
                  <div className="min-w-0"><span className="text-slate-500">{f.label}:</span> <span className="text-slate-800 font-medium">{f.value ?? "—"}</span>
                    {f.evidence && <span className="block text-[10px] text-slate-400 italic truncate">“{f.evidence}”</span>}
                  </div>
                </div>
              ))}
              {facts.length === 0 && <div className="text-[11px] text-slate-400">No facts extracted.</div>}
            </div>
          </div>

          {/* Section 3 — Observations */}
          {obs.length > 0 && (
            <div data-testid="intelligence-observations">
              <div className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Observations ({obs.length})</div>
              <div className="space-y-1">
                {obs.map((o) => (
                  <div key={o.id} className="text-[11px] flex items-start gap-1.5" data-testid="intelligence-observation">
                    <Icons.Dot className={`h-4 w-4 shrink-0 ${SEV[o.severity] || SEV.info}`} />
                    <div className="min-w-0"><span className="text-slate-700">{o.text}</span>
                      {o.evidence && <span className="block text-[10px] text-slate-400 italic truncate">“{o.evidence}”</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Decision Trace */}
          {trace && (
            <div>
              <button onClick={() => setShowTrace((v) => !v)} data-testid="intelligence-trace-toggle" className="text-[10px] text-slate-400 inline-flex items-center gap-1 hover:text-slate-600">
                <Icons.ScrollText className="h-3 w-3" />AI Decision Trace
                <Icons.ChevronDown className={`h-3 w-3 transition-transform ${showTrace ? "rotate-180" : ""}`} />
              </button>
              {showTrace && (
                <div className="mt-1 text-[10px] text-slate-500 rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-2 space-y-1" data-testid="intelligence-trace">
                  <div><span className="text-slate-400">Engine:</span> {trace.engine} · {trace.status}</div>
                  <div><span className="text-slate-400">Model:</span> {pkg.intelligence_model_used} · {pkg.intelligence_escalation_reason}</div>
                  {trace.applicable_policies?.map((p, i) => <div key={i} className="flex gap-1"><span className="text-slate-300">•</span>{p}</div>)}
                  <div className="text-slate-400">Inputs: {(trace.inputs_consumed || []).join(", ")}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
